import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useEffect, useState } from 'react';
import { NewAccountStatus, NewAccountCreationSource } from 'constants/new_accounts.constants';
import { useNavigate } from 'react-router-dom';
import { setNewAccount } from 'state/reducers/new_accounts.reducers';
import { pagesEnum } from 'constants/ui.constants';
import {
	getNewBankAccountByAccountId,
	getNewUsersByAccountId,
	deleteNewAccountAndCompany,
} from 'services/new_accounts.service';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getCustomerWithTranslatedType } from './helper';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog';

type IProps = {
	inReviewAccounts: Record<string, any>;
};

export const WaitingForApprovalTable: React.FC<IProps> = ({ inReviewAccounts }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const getNewUsersByAccountIdPermission = hasPermission([permissionEnums.GET_NEW_USERS]);
	const getNewBankAccountByAccountIdPermission = hasPermission([permissionEnums.GET_NEW_BANK_ACCOUNTS]);
	const deleteAccountPermission = hasPermission([permissionEnums.REMOVAL_ACCOUNT]);
	const [accounts, setAccounts] = useState<any>([]);

	const { dialogConfig, openDialog, handleConfirm, handleCancel } = useConfirmationDialog();

	useEffect(() => {
		if (inReviewAccounts) {
			const data = getCustomerWithTranslatedType(inReviewAccounts, t);
			setAccounts(data);
		}
	}, [inReviewAccounts]);

	const handleDeleteClick = (accountId: number) => {
		const account = accounts.find((acc: any) => acc.id === accountId);
		if (!account) return;

		openDialog({
			title: t('page.newAccount.delete.confirmation.title'),
			message: t('page.newAccount.delete.confirmation.message').replace(
				'{accountName}',
				account.accountNameHebrew || account.accountNameEnglish
			),
			confirmLabel: t('actions.deleteAccount'),
			cancelLabel: t('actions.cancel'),
			onConfirm: async () => {
				try {
					await dispatch(deleteNewAccountAndCompany(accountId));
				} catch (error) {
					console.error('Failed to delete account:', error);
				}
			},
		});
	};

	return (
		<>
			<Stack spacing={2}>
				<Typography variant="subtitle" color={theme.palette.root.grey}>
					{inReviewAccounts?.length + ' ' + t('messages.results')}
				</Typography>
				{inReviewAccounts?.length > 0 && (
					<ClientPaginatedTable
						data={TableConfig.tableData(accounts)}
						rowAction={(row) => {
							const isHyperRecoveryRow = row.creationSource === NewAccountCreationSource.HyperWebhook;

							if (row?.isNotCreatedByMe || isHyperRecoveryRow) {
								dispatch(setNewAccount(row));
								if (getNewUsersByAccountIdPermission) {
									dispatch(getNewUsersByAccountId(row?.id));
								}
								if (getNewBankAccountByAccountIdPermission) {
									dispatch(getNewBankAccountByAccountId(row?.id));
								}
								navigate(pagesEnum.NEW_ACCOUNT + '/review/' + row?.id);
							}
						}}
						columns={TableConfig.buildColumns(
							NewAccountStatus.IN_REVIEW,
							t,
							deleteAccountPermission ? handleDeleteClick : undefined
						)}
					/>
				)}
			</Stack>

			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				message={dialogConfig.message}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				confirmColor="error"
				loading={dialogConfig.loading}
				error={dialogConfig.error}
			/>
		</>
	);
};
